import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Input,
  Button,
  Typography,
  Avatar,
  Spin,
  Badge,
  FloatButton,
} from 'antd';
import {
  SendOutlined,
  MessageOutlined,
  CloseOutlined,
  MinusOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { chatService, ChatMessage as ChatMessageType } from '../../services/chatService';
import api from '../../services/api';

interface FloatingChatProps {
  token: string;
  userId: string;
  userName: string;
}

const FloatingChat: React.FC<FloatingChatProps> = ({ token, userId, userName }) => {
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [typingUsers, setTypingUsers] = useState<Map<number, string>>(new Map());
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadChatHistory = async () => {
    try {
      const response = await api.get('/chat/history?limit=100');

      if (response.data?.success) {
        const fetchedMessages = Array.isArray(response.data.data)
          ? response.data.data
          : (Array.isArray(response.data.messages) ? response.data.messages : []);
        setMessages(fetchedMessages);
        scrollToBottom();
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Erreur chargement historique chat:', error);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Connexion au service de chat
    chatService.connect(token, userId);

    // Charger l'historique
    loadChatHistory();

    // Écouter les nouveaux messages
    chatService.onMessage((message) => {
      setMessages((prev) => [...(Array.isArray(prev) ? prev : []), message]);
      scrollToBottom();
      
      // Incrémenter le compteur de non lus si la fenêtre est fermée
      if (!isOpen && message.sender_role !== 'admin') {
        setUnreadCount((prev) => prev + 1);
      }
    });

    // Écouter les changements de connexion
    chatService.onConnectionChange((connected) => {
      setIsConnected(connected);
    });

    // Écouter les indicateurs de frappe
    chatService.onTyping((data) => {
      setTypingUsers((prev) => {
        const newMap = new Map(prev);
        newMap.set(data.userId, data.userName);
        return newMap;
      });
    });

    chatService.onStopTyping((data) => {
      setTypingUsers((prev) => {
        const newMap = new Map(prev);
        newMap.delete(data.userId);
        return newMap;
      });
    });

    return () => {
      // Ne pas déconnecter le socket partagé au démontage du composant
      chatService.clearMessageCallbacks();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId]);

  // Réinitialiser le compteur quand on ouvre le chat
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSendMessage = () => {
    if (inputMessage.trim() === '') return;

    chatService.sendMessage(inputMessage);
    setInputMessage('');
    chatService.stopTyping();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputMessage(e.target.value);

    chatService.startTyping();

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      chatService.stopTyping();
    }, 2000);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const toggleChat = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const renderMessage = (message: ChatMessageType) => {
    const isAdmin = message.sender_role === 'admin' || message.sender_role === 'manager';
    const senderName = message.sender?.name || message.sender?.username || 'Utilisateur';

    return (
      <div
        key={message.id}
        style={{
          display: 'flex',
          justifyContent: isAdmin ? 'flex-end' : 'flex-start',
          marginBottom: 12,
        }}
      >
        {!isAdmin && (
          <Avatar
            style={{
              backgroundColor: '#2196f3',
              width: 28,
              height: 28,
              marginRight: 8,
              fontSize: '0.875rem',
            }}
          >
            {senderName.charAt(0).toUpperCase()}
          </Avatar>
        )}
        <div style={{ maxWidth: '75%' }}>
          {!isAdmin && (
            <Typography.Text type="secondary" style={{ marginLeft: 4, marginBottom: 2, display: 'block', fontSize: '0.75rem' }}>
              {senderName}
            </Typography.Text>
          )}
          <Card
            size="small"
            style={{
              padding: '10px',
              backgroundColor: isAdmin ? '#0084ff' : '#e4e6eb',
              color: isAdmin ? 'white' : 'inherit',
              borderRadius: '18px',
              border: 'none',
            }}
            bodyStyle={{ padding: 0 }}
          >
            <Typography.Text style={{ fontSize: '0.9rem', color: isAdmin ? 'white' : 'inherit' }}>
              {message.message}
            </Typography.Text>
            <Typography.Text
              style={{
                color: isAdmin ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.45)',
                fontSize: '0.65rem',
                marginTop: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              {formatTime(message.created_at)}
              {isAdmin && (
                <span style={{ 
                  marginLeft: 4, 
                  display: 'inline-flex', 
                  alignItems: 'center',
                  position: 'relative',
                  width: message.is_read ? 16 : 10,
                  height: 10,
                }}>
                  <CheckOutlined style={{ 
                    fontSize: 10, 
                    color: message.is_read ? '#4fc3f7' : 'rgba(255,255,255,0.7)',
                    position: 'absolute',
                    left: 0,
                  }} />
                  {message.is_read && (
                    <CheckOutlined style={{ 
                      fontSize: 10, 
                      color: '#4fc3f7',
                      position: 'absolute',
                      left: 5,
                    }} />
                  )}
                </span>
              )}
            </Typography.Text>
          </Card>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Bouton flottant */}
      {!isOpen && (
        <Badge count={unreadCount} offset={[-10, 10]}>
          <FloatButton
            icon={<MessageOutlined style={{ fontSize: 28 }} />}
            type="primary"
            style={{
              width: 60,
              height: 60,
              bottom: 24,
              right: 24,
            }}
            onClick={toggleChat}
          />
        </Badge>
      )}

      {/* Fenêtre de chat */}
      {isOpen && (
        <Card
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 380,
            height: isMinimized ? 56 : 520,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            overflow: 'hidden',
            zIndex: 1300,
            transition: 'height 0.3s ease',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }}
          bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          {/* En-tête */}
          <div
            style={{
              backgroundColor: '#0084ff',
              color: 'white',
              padding: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={handleMinimize}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MessageOutlined />
              <div>
                <Typography.Text strong style={{ color: 'white', display: 'block' }}>
                  Chat Support
                </Typography.Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 8, color: isConnected ? '#4caf50' : '#f44336' }}>●</span>
                  <Typography.Text style={{ fontSize: '0.7rem', color: 'white' }}>
                    {isConnected ? 'En ligne' : 'Hors ligne'}
                  </Typography.Text>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <Button
                type="text"
                size="small"
                style={{ color: 'white' }}
                icon={<MinusOutlined />}
                onClick={(e) => { e.stopPropagation(); handleMinimize(); }}
              />
              <Button
                type="text"
                size="small"
                style={{ color: 'white' }}
                icon={<CloseOutlined />}
                onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
              />
            </div>
          </div>
          {/* Contenu du chat (masqué si minimisé) */}
          {!isMinimized && (
            <>
              {/* Zone des messages */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: 16,
                  backgroundColor: '#f0f2f5',
                }}
              >
                {isLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin size="large" />
                  </div>
                ) : (messages?.length ?? 0) === 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: 'rgba(0,0,0,0.45)',
                    }}
                  >
                    <MessageOutlined style={{ fontSize: 48, marginBottom: 8, opacity: 0.3 }} />
                    <Typography.Text>Aucun message</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: '0.875rem' }}>Commencez une conversation</Typography.Text>
                  </div>
                ) : (
                  <>
                    {(messages || []).map((message) => renderMessage(message))}
                    <div ref={messagesEndRef} />
                  </>
                )}

                {/* Indicateur de frappe */}
                {typingUsers.size > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
                    <Avatar style={{ width: 24, height: 24, marginRight: 8, backgroundColor: '#2196f3' }}>
                      <Typography.Text style={{ fontSize: '0.75rem' }}>•••</Typography.Text>
                    </Avatar>
                    <Typography.Text type="secondary" italic style={{ fontSize: '0.875rem' }}>
                      {Array.from(typingUsers.values())[0]} écrit...
                    </Typography.Text>
                  </div>
                )}
              </div>

              {/* Zone de saisie */}
              <div
                style={{
                  padding: 12,
                  backgroundColor: 'white',
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Tapez un message..."
                  value={inputMessage}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  disabled={!isConnected}
                  style={{
                    borderRadius: '20px',
                    backgroundColor: '#f0f2f5',
                    border: 'none',
                  }}
                />
                <Button
                  type="primary"
                  shape="circle"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  disabled={!isConnected || inputMessage.trim() === ''}
                  size="small"
                  style={{
                    backgroundColor: '#0084ff',
                  }}
                />
              </div>
            </>
          )}
        </Card>
      )}
    </>
  );
};

export default FloatingChat;
